import { ref, computed } from 'vue';
import { defineStore } from "pinia";
import { useUserStore  }from "./user";
import { useAsyncState } from '@vueuse/core';
import { useChargeManagement } from './charge-management';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { useErrorOverlay } from './overlays/error-overlay';

type Timeout = ReturnType<typeof setInterval>;

export type CouponMetadata = {
  percentage_reduction?: string
  price?: string;
};
export type Coupon = {
  id: number;
  code: string;
  is_singleuse: boolean;
  is_active: boolean;
  ctype: "PERCENT" | "FIXED" | "FREE";
  metadata: CouponMetadata | null;
};

export type VerifyCouponReturnType = Coupon | null | "Invalid" | "Error";

type ActiveChargeType = {
  id: number;
  start: string;
  stop: string;
  user: {
    id: number;
    username: string;
  };
  last_nonzero_power_time: string;
  energy_wh: number;
  last_power: {
    chargingstation: number;
    power: number;
    timestamp: string;
  };
  coupon?: Coupon;
};

export type CustomMessageType = Record<string, any>;

export type ChargingStation = {
  qrcodeid: number;
  active_charge: ActiveChargeType;
  park: number;
  park_bnum: number;
  latitude: number;
  longitude: number;
  accept_guests: boolean;
  is_payment_configured: boolean;
  price: number;
  override_logo_url: string;
  show_phrases_frontend: boolean;
  logged_user: CustomMessageType;
  has_charge_permission: boolean;
  show_custom_message: boolean;
  custom_message: CustomMessageType;
  evse_id: string;
  country_alpha2: string;
  custom_message_fallback_lang: string;
  imprint_cents: number;
  google_pay: boolean;
  coupon_enabled: boolean;
  last_power_timestamp: string;
  suspended?: boolean;
  test_mode?: boolean;
};

export const useStation = defineStore("station", () => {
  const user = useUserStore();
  const route = useRoute();
  const router = useRouter();
  const error_overlay = useErrorOverlay();
  const charge_management = useChargeManagement();

  /** State **/
  const chargingstation_ = ref<ChargingStation | null>(null);
  const refresh_interval_id = ref<Timeout | null>(null);
  const total_Wh_ = ref<number>(0);
  const power_ = ref<number>(0);
  const local_coupon = ref<Coupon | null>(null);

  /** Getters **/
  const chargingstation = computed((): ChargingStation | null => chargingstation_.value);
  const total_Wh = computed(() => total_Wh_.value);
  const power = computed(() => power_.value);
  const qrcode = computed((): number | null => chargingstation.value?.qrcodeid ?? null);
  const location = computed((): [number, number] | null => {
    const cs = chargingstation.value;
    if (cs === null) { return null; }
    return [cs.latitude, cs.longitude];
  });
  const price = computed((): number | null => chargingstation.value?.price ?? null);
  const active_charge = computed((): ActiveChargeType | null => chargingstation.value?.active_charge ?? null);
  const charging = computed((): boolean => active_charge.value !== null);
  const username = computed((): string | null => active_charge.value?.user.username ?? null);
  const want_quotes = computed((): boolean => chargingstation.value?.show_phrases_frontend ?? true);
  const logo = computed((): string | null => chargingstation.value?.override_logo_url ?? null);
  const accept_guests = computed((): boolean => {
    const cs = chargingstation.value;
    if (cs === null) { return false; }
    const { accept_guests, is_payment_configured, price } = cs;
    return accept_guests && is_payment_configured && price > 0;
  });
  const evse_id = computed((): string => chargingstation.value?.evse_id ?? "");
  const custom_message = computed((): { text: CustomMessageType, fallback_locale: string} | null => {
    const cs = chargingstation.value;
    if (cs === null) { return null; }
    return { text: cs.custom_message, fallback_locale: cs.custom_message_fallback_lang || "en" };
  });
  const country = computed((): string => chargingstation.value?.country_alpha2.toUpperCase() ?? "FR");
  const max_wallet_imprint_cents = computed((): number => chargingstation.value?.imprint_cents ?? 60);
  const allow_gpay = computed((): boolean => {
    const cs = chargingstation.value;
    return cs === null ? false : accept_guests.value && cs.google_pay;
  });
  const is_offline = computed((): boolean => {
    const cs = chargingstation.value;
    if (cs === null) { return false; }
    if (cs.last_power_timestamp === null) return true;
    const lastUpdate: Date = new Date(cs.last_power_timestamp);
    return Date.now() - lastUpdate.getTime() > 5 * 60 * 1000; // no updates in 5 minutes => the park is offline
  });
  const is_suspended = computed((): boolean => {
    const cs = chargingstation.value;
    if (cs === null) { return false; }
    return (! charging.value) && (cs.suspended ?? false);
  });
  const coupon_enabled = computed((): boolean => chargingstation.value?.coupon_enabled ?? false);
  const coupon = computed((): Coupon | null =>  {
    if (local_coupon.value !== null) {
      return local_coupon.value;
    }
    if (active_charge.value !== null) {
      return active_charge.value.coupon ?? null;
    }
    return null;
  });
  const test_mode = computed((): boolean => chargingstation.value?.test_mode ?? false);

  /** Actions **/

  // Perform asynchronoulsy the fetch operation
  async function do_fetch(id: number | null, init: boolean) {
    if (id === null) {
      console.error("[station.fetch]: fetching a null qrcode");
      return false;
    }
    try {
      const response = await axios.get<ChargingStation>(`/v2/chargecontroller/${id}/`);
      const station : ChargingStation = response.data;
      console.log(`[station.fetch]: fetched cs ${station.qrcodeid}`);
      chargingstation_.value = station;
      localStorage.setItem( "last_cp", JSON.stringify({ qr: id, timestamp: Date.now() }));

      if (init) {
        await user.fetch();
      }
      const owner = (charging.value && active_charge.value && active_charge.value.user.id === user.id) ?? false;
      await enable_refresh_station_infos(owner);
      if (active_charge.value !== null) {
        total_Wh_.value = active_charge.value.energy_wh;
        if (active_charge.value.last_power !== null) {
          power_.value = active_charge.value.last_power.power;
        }
        if (total_Wh_.value > 0) {
          charge_management.clear_warning_interval();
        }
      }
      return true;
    } catch (err: any) {
      console.error("[station.fetch]: error: ", err);
      switch (err.status) {
      case 408:
        console.error("[station.fetch]: fetch timeout");
        error_overlay.show_timeout();
        break;
      case 401:
      case 403:
        console.error(`[station.fetch]: fetch failed with status ${err.status}, message '${err.customMessage}'`);
        if (route.name !== "login") {
          await router.push("/login")
        }
        break;
      default:
        error_overlay.show(err.customMessage, err.status);
        break;
      }
      return false;
    }
  }

  function update_refresh_station_interval_id(id: Timeout | null): void {
    if (id === null) {
      console.log("[station.set_interval_id]: resetting to null");
      const refresh: Timeout | undefined = refresh_interval_id.value ?? undefined;
      clearInterval(refresh);
    } else {
      console.log(`[station.set_interval_id]: setting to ${id}`);
    }
    refresh_interval_id.value = id;
  }

  async function enable_refresh_station_infos(owner: boolean) : Promise<void> {
    const timeout: number = parseInt(
      owner ? import.meta.env.VITE_APP_STATION_OWNER_REFRESH_INTERVAL
            : import.meta.env.VITE_APP_STATION_OBSERVER_REFRESH_INTERVAL
    );
    console.log(
      `[station.start_refresh]: enable_refresh_station_info: timeout for ${owner ? "owner" : "observer"} is ${timeout}`
    );
    if (owner) update_refresh_station_interval_id(null);
    if (refresh_interval_id.value === null) {
      const interval_id = setInterval(async () => {
        await do_fetch(qrcode.value, false);
      }, timeout);

      // Store the intervalId into vuex store
      update_refresh_station_interval_id(interval_id);
    }
  }

  // NOTE: probably unused, since `do_fetch` is used everywhere
  function fetch(id: number | null, init: boolean) {
      if (id === undefined || id === null) {
        console.log(`[station.fetch] failed fetch: qrcode is ${id}`);
        return;
      }
      console.log(
        `[station.fetch]: start fetch station with qrcode ${qrcode} (${init ? "init" : "update"})`
      );
      const { state, isLoading } = useAsyncState(do_fetch(id, init), false);
      return { state, loading: isLoading };
  }


  async function use_coupon(code: string): Promise<VerifyCouponReturnType> {
    console.log("[station.use_coupon]: verifying coupon");
    if (code === null || code.length == 0) {
      console.log("[station.use_coupon]: null or incomplete coupon code");
      return null;
    }
    if (code.length < import.meta.env.VITE_APP_COUPON_LENGTH) {
      console.log("[station.use_coupon]: invalid coupon code");
      return "Invalid";
    }
    try {
      const response = await axios.get<Coupon>(
        `/public/1/chargecontroller/${qrcode.value}/coupon/${code}/`
      );
      const data : Coupon = response.data;
      if (code !== data.code) {
        console.error("[station.use_coupon]: received coupon for a different code");
        // TODO: raise error overlay
        return "Error";
      }
      console.log("[station.use_coupon]: coupon received");
      local_coupon.value = data;
      return data;
    } catch (err: any) {
      if (err.status === 400) {
        return "Error";
      }
      // TODO: raise error overlay with some kind of error
      return "Error";
    }
  }

  return {
    // chargingstation,
    total_Wh, power, coupon,
    qrcode, location, price, active_charge, charging, username, want_quotes, coupon_enabled,
    logo, accept_guests, evse_id, custom_message, country, max_wallet_imprint_cents, allow_gpay,
    is_offline, is_suspended, use_coupon, test_mode,

    fetch, do_fetch, enable_refresh_station_infos,
  }
});
